<template>
  <div class="attendance-indication mb-3">
    <div class="d-flex align-items-center justify-content-between mb-2">
      <div>
        <div class="font-weight-bold indication-title">
          {{ $t("activityPaymentAttendance.title") }}
        </div>
        <div class="text-muted indication-subtitle">
          {{ $t("activityPaymentAttendance.subtitle") }}
        </div>
      </div>
      <a-button
        type="primary"
        ghost
        size="small"
        :loading="loading"
        @click="load"
      >
        {{ $t("activityPaymentAttendance.refresh") }}
      </a-button>
    </div>

    <a-range-picker
      v-model="range"
      format="DD/MM/YYYY"
      style="width: 280px; margin-bottom: 12px"
      @change="onRangeChange"
    />

    <a-spin :spinning="loading">
      <a-alert
        v-if="attendance && !attendance.hasSessions"
        type="warning"
        show-icon
        class="mb-2"
        :message="$t('activityPaymentAttendance.noSessions')"
        :description="$t('activityPaymentAttendance.noSessionsHint')"
      />

      <div v-else-if="attendance" class="attendance-buckets">
        <div class="attendance-bucket present">
          <div class="bucket-head">
            <span>{{ $t("activityPaymentAttendance.present") }}</span>
            <a-badge
              :count="attendance.present.count"
              :number-style="{ backgroundColor: '#52c41a' }"
            />
          </div>
          <div v-if="attendance.present.dates.length" class="bucket-dates">
            <a-tag
              v-for="date in attendance.present.dates"
              :key="'p-' + date"
              color="green"
            >
              {{ formatDate(date) }}
            </a-tag>
          </div>
          <div v-else class="bucket-empty">
            {{ $t("activityPaymentAttendance.none") }}
          </div>
        </div>

        <div class="attendance-bucket retard">
          <div class="bucket-head">
            <span>{{ $t("activityPaymentAttendance.retard") }}</span>
            <a-badge
              :count="attendance.retard.count"
              :number-style="{ backgroundColor: '#fa8c16' }"
            />
          </div>
          <div v-if="attendance.retard.dates.length" class="bucket-dates">
            <a-tag
              v-for="date in attendance.retard.dates"
              :key="'r-' + date"
              color="orange"
            >
              {{ formatDate(date) }}
            </a-tag>
          </div>
          <div v-else class="bucket-empty">
            {{ $t("activityPaymentAttendance.none") }}
          </div>
        </div>

        <div class="attendance-bucket absence">
          <div class="bucket-head">
            <span>{{ $t("activityPaymentAttendance.absence") }}</span>
            <a-badge
              :count="attendance.absence.count"
              :number-style="{ backgroundColor: '#f5222d' }"
            />
          </div>
          <div v-if="attendance.absence.dates.length" class="bucket-dates">
            <a-tag
              v-for="date in attendance.absence.dates"
              :key="'a-' + date"
              color="red"
            >
              {{ formatDate(date) }}
            </a-tag>
          </div>
          <div v-else class="bucket-empty">
            {{ $t("activityPaymentAttendance.none") }}
          </div>
        </div>

        <div class="attendance-bucket exclus">
          <div class="bucket-head">
            <span>{{ $t("activityPaymentAttendance.exclus") }}</span>
            <a-badge
              :count="attendance.exclus.count"
              :number-style="{ backgroundColor: '#722ed1' }"
            />
          </div>
          <div v-if="attendance.exclus.dates.length" class="bucket-dates">
            <a-tag
              v-for="date in attendance.exclus.dates"
              :key="'e-' + date"
              color="purple"
            >
              {{ formatDate(date) }}
            </a-tag>
          </div>
          <div v-else class="bucket-empty">
            {{ $t("activityPaymentAttendance.none") }}
          </div>
        </div>

        <div class="attendance-totals text-muted">
          {{
            $t("activityPaymentAttendance.totals", {
              sessions: attendance.sessionCount,
              present: attendance.present.count,
              retard: attendance.retard.count,
              absence: attendance.absence.count,
              exclus: attendance.exclus.count,
            })
          }}
        </div>
      </div>
    </a-spin>
  </div>
</template>

<script>
import moment from "moment";
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";

export default {
  name: "DailyPaymentAttendanceIndication",
  props: {
    studentId: { type: String, required: true },
    activityId: { type: String, required: true },
  },
  data() {
    return {
      range: [moment().startOf("month"), moment().endOf("month")],
      loading: false,
      attendance: null,
    };
  },
  watch: {
    studentId() {
      this.load();
    },
    activityId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "";
    },
    onRangeChange(value) {
      if (!value || value.length !== 2) return;
      this.load();
    },
    async load() {
      if (!this.studentId || !this.activityId) {
        this.attendance = null;
        return;
      }
      if (!this.range || this.range.length !== 2) {
        this.$message.warning(this.$t("activityPaymentAttendance.needDates"));
        return;
      }

      this.loading = true;
      try {
        const res = await apiClient.post(
          "/activityAttendance/paymentAttendance",
          {
            student: String(this.studentId),
            activity: String(this.activityId),
            from: moment(this.range[0]).format("YYYY-MM-DD"),
            to: moment(this.range[1]).format("YYYY-MM-DD"),
          }
        );
        this.attendance = res.data;
      } catch (e) {
        this.attendance = null;
        showApiError(this, e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.attendance-indication {
  border: 1px solid #91d5ff;
  border-radius: 8px;
  padding: 14px 16px;
  background: #e6f7ff;
}

.indication-title {
  font-size: 15px;
  color: #0050b3;
}

.indication-subtitle {
  font-size: 12px;
}

.attendance-buckets {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.attendance-bucket {
  background: #fff;
  border-radius: 6px;
  padding: 10px 12px;
  border: 1px solid #f0f0f0;
  min-height: 72px;
}

.attendance-bucket.present {
  border-left: 3px solid #52c41a;
}

.attendance-bucket.retard {
  border-left: 3px solid #fa8c16;
}

.attendance-bucket.absence {
  border-left: 3px solid #f5222d;
}

.attendance-bucket.exclus {
  border-left: 3px solid #722ed1;
}

.bucket-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 13px;
}

.bucket-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.bucket-empty {
  color: #8c8c8c;
  font-size: 12px;
}

.attendance-totals {
  grid-column: 1 / -1;
  font-size: 12px;
}

@media (max-width: 576px) {
  .attendance-buckets {
    grid-template-columns: 1fr;
  }
}
</style>
